import { getIdTokenParsed } from '../utils/auth';
import { getStbClient } from './fetch';
import { logError } from '../utils/logger';

interface UploadFilesResponse {
  id: string;
  size: number;
  originalname: string;
  fieldname: string;
  encoding: string;
  mimetype: string;
  timestamp: number;
  url: string;
  fileId: string;
  fileName: string;
}

export type FileReference = {
  _id: number | null;
  reference: string | null;
  name: string;
  url: string;
  fileSize: number | null;
  mimeType: string | null;
  timestamp: number | null;
  originalName: string | null;
  fileId: string;
  fileName: string;
  preferredUsername: string;
};

export type FileUploadRequest = {
  name: string;
  file: File;
  isValid: boolean;
};

export async function uploadFiles(
  files: Array<FileUploadRequest>
): Promise<Array<FileReference>> {
  const passedToken = await getIdTokenParsed();
  const preferredUsername = passedToken?.preferred_username ?? '';

  const checkStatusOnFile = async (
    resolve: (value?: unknown) => void,
    reject: (value?: unknown) => void,
    file: UploadFilesResponse
  ) => {
    try {
      const client = await getStbClient();

      const scannedResponse = await client.get(
        `/shared/documents/files/scanned/${preferredUsername}/${file.fileId}`
      );

      switch (scannedResponse.status) {
        case 200:
          resolve();
          break;
        case 204:
          setTimeout(() => {
            checkStatusOnFile(resolve, reject, file);
          }, 2500);
          break;
        default:
          logError(`Upload failed with status: ${scannedResponse.statusText}`);
          reject(
            new Error(`Unexpected scanned status ${scannedResponse.status}`)
          );
          break;
      }
    } catch (error) {
      reject(error);
    }
  };

  const uploadFilesResponse = (
    await Promise.all(
      files.map(async file => {
        const formData = new FormData();
        formData.append('file', file.file, file.name);

        const client = await getStbClient();

        if (!preferredUsername) {
          throw new Error('preferred_username is not available.');
        }

        const response = await client.post<UploadFilesResponse[]>(
          '/shared/documents/file',
          formData,
          {
            timeout: 2 * 60 * 1000
          }
        );

        if (response.status === 200) {
          const filesInResponse = response.data;

          await Promise.all(
            filesInResponse.map(
              fileInResponse =>
                new Promise((resolve, reject) => {
                  checkStatusOnFile(resolve, reject, fileInResponse);
                })
            )
          );
        } else {
          logError(
            `Upload failed with status: ${response.statusText}`,
            response
          );
          throw new Error(`Upload failed with status: ${response.status}`);
        }

        // editing first element as we only send one file at time
        return [{ ...response.data[0], fileName: file.name }];
      })
    )
  ).flat();

  // Size and mimetype are no longer returned from file uploads, and need to be fetched from the direct file input.
  const updatedUploadFilesResponse = uploadFilesResponse.map(fileReference => {
    const matchingFileUpload = files.find(
      fileUpload => fileUpload.name === fileReference.fileName
    );

    return matchingFileUpload
      ? {
          ...fileReference,
          size: matchingFileUpload.file.size,
          mimetype: matchingFileUpload.file.type
        }
      : fileReference;
  });

  return updatedUploadFilesResponse.map(fileReferences => ({
    _id: null,
    reference: fileReferences.fileId,
    name: fileReferences.fileName,
    url: fileReferences.url,
    fileSize: fileReferences.size,
    mimeType: fileReferences.mimetype,
    timestamp: fileReferences.timestamp,
    originalName: '',
    fileId: fileReferences.fileId,
    fileName: fileReferences.fileName,
    preferredUsername
  }));
}
